<script setup lang="ts">
import {
  TaskStatus,
  OrganisationAgentTaskType,
  type OrganisationAgentTaskInDb,
  type OrganisationAgentTaskSearchQuery,
} from '@/api';
import {
  DataTableColumns,
  type DataTableSortState,
  NTag,
  NBadge,
  NIcon,
  NButton,
  NTooltip,
} from 'naive-ui';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaFilterPanel, {
  type FilterConfig,
} from '@/components/design/composite/GaiaFilterPanel.vue';
import { IconFilter, IconRedo, IconRefresh } from '@/components/design/icons';
import { OrganisationAgentTaskService } from '@/services/organisationAgentTaskService';
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const message = useGaiaToast();

// Table state
const loading = ref<boolean>(false);
const searchQuery = ref<string>('');
const tasks = ref<OrganisationAgentTaskInDb[]>([]);

// Pagination state
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const totalCount = ref<number>(0);

// Filter state
const showFilterBar = ref<boolean>(false);
const filterOptions = ref<Record<string, (string | null)[]>>({});
const activeFilters = ref<Record<string, unknown>>({});

const filterConfig = computed<FilterConfig[]>(() => [
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'started_by', label: 'Started by' },
]);

// Sorting state
const sortKey = ref<string>('created_at');
const sortDirection = ref<'asc' | 'desc'>('desc');
const sortActivated = ref<boolean>(false);

const paginationConfig = computed(() => ({
  page: currentPage.value,
  pageSize: pageSize.value,
  itemCount: totalCount.value,
  showSizePicker: true,
  pageSizes: [5, 10, 25, 50],
  onUpdatePage: handlePageChange,
  onUpdatePageSize: handlePageSizeChange,
}));

// Human-readable labels for the task type; unknown types degrade to a
// title-cased fallback so new backend types still render sensibly.
const TASK_TYPE_LABELS: Record<string, string> = {
  [OrganisationAgentTaskType.CONTRACT_MANAGEMENT_REPOPULATE_CONTRACT]:
    'Repopulate contract details',
};

function humanizeType(type: string): string {
  return (
    TASK_TYPE_LABELS[type] ??
    type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
}

const STATUS_META: Record<
  TaskStatus,
  { label: string; type: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  [TaskStatus.PENDING]: { label: 'Pending', type: 'default' },
  [TaskStatus.RUNNING]: { label: 'Running', type: 'info' },
  [TaskStatus.DONE]: { label: 'Done', type: 'success' },
  [TaskStatus.FAILED]: { label: 'Failed', type: 'error' },
  [TaskStatus.CANCELLED]: { label: 'Cancelled', type: 'default' },
  [TaskStatus.PARTIAL]: { label: 'Partial', type: 'warning' },
  [TaskStatus.SKIPPED]: { label: 'Skipped', type: 'default' },
  [TaskStatus.WARNING]: { label: 'Warning', type: 'warning' },
};

// Terminal states the backend accepts a retry from. SKIPPED is deliberate (e.g. empty
// file) and DONE re-runs go through the admin whole-intake restart, so neither is
// retryable here.
const RETRYABLE_STATUSES = new Set<TaskStatus>([
  TaskStatus.FAILED,
  TaskStatus.CANCELLED,
  TaskStatus.WARNING,
]);

const retryingUids = ref<Set<string>>(new Set());

async function retryTask(row: OrganisationAgentTaskInDb): Promise<void> {
  retryingUids.value.add(row.uid);
  try {
    await OrganisationAgentTaskService.retry(row.uid);
    message.success('Task queued for retry');
    await fetchTasks();
  } catch (error) {
    logger.error('Error retrying agent task:', error);
    message.error('Failed to retry task');
  } finally {
    retryingUids.value.delete(row.uid);
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function startedByLabel(row: OrganisationAgentTaskInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
  return row.started_by_user?.full_name || row.started_by_user?.email || row.started_by || '—';
}

// Build column filters for API
function buildColumnFilters(): { [key: string]: string[] } {
  const filters: { [key: string]: string[] } = {};

  for (const [key, value] of Object.entries(activeFilters.value)) {
    if (Array.isArray(value) && value.length > 0) {
      const convertedValues = value
        .map(v => (v === '(Empty)' ? null : String(v)))
        .map(v => v ?? '');

      if (convertedValues.length > 0) {
        filters[key] = convertedValues;
      }
    }
  }

  return filters;
}

async function fetchTasks(): Promise<void> {
  loading.value = true;
  try {
    const query: OrganisationAgentTaskSearchQuery = {
      text: searchQuery.value || '',
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: sortKey.value,
      sort_direction: sortDirection.value,
      similarity_threshold: 0.5,
      column_filters: buildColumnFilters(),
    };

    const result = await OrganisationAgentTaskService.search(query);
    if (!result) return;

    tasks.value = result.results;
    totalCount.value = result.result_count;

    filterOptions.value = result.filter_options as Record<string, (string | null)[]>;
  } catch (error) {
    logger.error('Error fetching agent tasks:', error);
    message.error('Failed to load agent tasks');
  } finally {
    loading.value = false;
  }
}

function handleSortChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (!sorter || Array.isArray(sorter)) return;

  if (sorter.order === false) {
    sortKey.value = 'created_at';
    sortDirection.value = 'desc';
    sortActivated.value = false;
    currentPage.value = 1;
    void fetchTasks();
    return;
  }

  sortActivated.value = true;
  sortKey.value = String(sorter.columnKey);
  sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  currentPage.value = 1;
  void fetchTasks();
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchTasks();
}

const hasActiveFilters = computed(() => Object.keys(activeFilters.value).length > 0);

watch(hasActiveFilters, newValue => {
  if (newValue && !showFilterBar.value) {
    showFilterBar.value = true;
  }
});

function toggleFilterBar(): void {
  showFilterBar.value = !showFilterBar.value;
}

function handleFiltersUpdate(filters: Record<string, unknown>): void {
  activeFilters.value = filters;
  currentPage.value = 1;
  void fetchTasks();
}

function handlePageChange(page: number): void {
  currentPage.value = page;
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
}

watch(currentPage, () => {
  if (currentPage.value > 0) void fetchTasks();
});

watch(pageSize, () => {
  if (pageSize.value > 0) void fetchTasks();
});

function sortOrderFor(key: string): 'ascend' | 'descend' | false {
  if (!sortActivated.value || sortKey.value !== key) return false;
  return sortDirection.value === 'asc' ? 'ascend' : 'descend';
}

const columns = computed((): DataTableColumns<OrganisationAgentTaskInDb> => [
  {
    title: 'Task',
    key: 'type',
    sorter: true,
    sortOrder: sortOrderFor('type'),
    render: (row): string => humanizeType(row.type),
  },
  {
    title: 'Status',
    key: 'status',
    sorter: true,
    sortOrder: sortOrderFor('status'),
    render: (row): VNode => {
      const meta = STATUS_META[row.status];
      const tag = h(
        NTag,
        { type: meta.type, size: 'small', round: true },
        { default: () => meta.label }
      );
      if (!row.error_message) return tag;
      return h(
        NTooltip,
        { trigger: 'hover', style: 'max-width: 420px' },
        {
          trigger: () => h(NBadge, { dot: true, type: meta.type }, { default: () => tag }),
          default: () => row.error_message,
        }
      );
    },
  },
  {
    title: 'Started by',
    key: 'started_by',
    sorter: true,
    sortOrder: sortOrderFor('started_by'),
    render: (row): string => startedByLabel(row),
  },
  {
    title: 'Created',
    key: 'created_at',
    sorter: true,
    sortOrder: sortOrderFor('created_at'),
    render: (row): string => formatDate(row.created_at),
  },
  {
    title: 'Updated',
    key: 'updated_at',
    render: (row): string => formatDate(row.updated_at),
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (row): VNode | null => {
      if (!RETRYABLE_STATUSES.has(row.status)) return null;
      return h(
        NButton,
        {
          size: 'small',
          loading: retryingUids.value.has(row.uid),
          onClick: () => {
            void retryTask(row);
          },
        },
        {
          icon: () => h(NIcon, null, { default: () => h(IconRedo) }),
          default: () => 'Retry',
        }
      );
    },
  },
]);

onMounted(fetchTasks);
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.agentTasks }}</n-breadcrumb-item>
    </template>

    <div
      style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        gap: 16px;
      "
    >
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        {{ navLabels.agentTasks }}
      </gaia-header>

      <div style="display: flex; gap: 12px; align-items: center; flex-shrink: 0">
        <n-button
          :type="hasActiveFilters ? 'primary' : 'default'"
          :quaternary="!hasActiveFilters"
          @click="toggleFilterBar"
        >
          <template #icon>
            <n-icon><IconFilter /></n-icon>
          </template>
          Filter
        </n-button>
        <gaia-search-input v-model:value="searchQuery" :debounce="500" @search="applySearch" />
        <n-button :loading="loading" @click="fetchTasks">
          <template #icon>
            <n-icon><IconRefresh /></n-icon>
          </template>
          Refresh
        </n-button>
      </div>
    </div>

    <gaia-filter-panel
      v-model:visible="showFilterBar"
      :filter-options="filterOptions"
      :active-filters="activeFilters"
      :filter-config="filterConfig"
      @update:filters="handleFiltersUpdate"
    />

    <gaia-table
      :columns="columns"
      :data="tasks"
      :loading="loading"
      :remote="true"
      :pagination="paginationConfig"
      :row-key="(row: OrganisationAgentTaskInDb) => row.uid"
      @update:sorter="handleSortChange"
    />
  </Layout>
</template>
